'use client'

import { CheckIcon, ChevronsUpDownIcon, SearchIcon, XIcon } from 'lucide-react'
import { useTranslations } from 'next-intl'
import {
  createContext,
  Fragment,
  type ReactNode,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react'

import { Button } from '@/components/ui/button'
import { InfoTip } from '@/components/ui/info-tip'
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { PlanLockTooltip, PlanStar } from '@/features/billing/components/plan-lock-tooltip'
import { TIME_FRAME_OPTIONS } from '@/features/chart/lib/utils/chart-util'
import {
  FormInput,
  FormSection,
  ORDER_FORM_FIELD_CLASS,
} from '@/features/order/order-form/components/widget/order-form-fields'
import { filterChipSuggestions } from '@/features/order/order-form/lib/chip-suggestions'
import type { OperatorToken } from '@/features/order/order-form/lib/trigger-condition-model'
import {
  OPERATOR_OPTIONS,
  type Option,
} from '@/features/order/order-form/lib/trigger-select-options'
import { useNumberInput } from '@/hooks/use-number-format'
import { cn } from '@/lib/utils'
import { formatSymbol } from '@/lib/utils/format-util'

// The trigger editor's fields use the order form's shared field surface (order-form-fields.tsx),
// so a condition's inputs look like every other order-type parameter.
export const ROW_INPUT_CLASS = `${ORDER_FORM_FIELD_CLASS} w-full text-right placeholder:text-foreground/30`
export const ROW_TRIGGER_CLASS = `${ORDER_FORM_FIELD_CLASS} w-full shadow-none [&>div>span]:flex-1 [&>div>span]:text-right`
export const SELECT_CONTENT_CLASS = 'z-[60] border-0 shadow-2xl backdrop-blur-2xl'

// ── Data context (instrument / asset / position-group options) ────────────────
export type ConditionEditorData = {
  symbolOptions: Option<string>[]
  assetOptions: Option<string>[]
  groupOptions: Option<string>[]
  accountOptions: Option<string>[]
  defaultSymbol: string
  defaultAccountId: string
}

const ConditionEditorDataContext = createContext<ConditionEditorData>({
  symbolOptions: [],
  assetOptions: [],
  groupOptions: [],
  accountOptions: [],
  defaultSymbol: '',
  defaultAccountId: '',
})

export function ConditionEditorDataProvider({
  value,
  children,
}: {
  value: ConditionEditorData
  children: ReactNode
}) {
  return (
    <ConditionEditorDataContext.Provider value={value}>
      {children}
    </ConditionEditorDataContext.Provider>
  )
}

export function useConditionEditorData(): ConditionEditorData {
  return useContext(ConditionEditorDataContext)
}

// ── Layout ────────────────────────────────────────────────────────────────────
export function FieldRow({
  label,
  help,
  children,
}: {
  label: string
  help?: string
  children: ReactNode
}) {
  return (
    <FormSection label={label} info={help}>
      {children}
    </FormSection>
  )
}

// ── Selects ───────────────────────────────────────────────────────────────────

/** A {@link TokenSelect} option that may be plan-gated (disabled + star + upsell tooltip). */
export type TokenSelectOption<T extends string> = Option<T> & {
  /** Render disabled/unselectable — e.g. gated behind a higher plan. */
  disabled?: boolean
  /** Gated behind a higher plan: starred, with an "available on <plan>" tooltip. */
  locked?: boolean
  /** Cheapest plan that unlocks it (catalog `plan_label`); defaults to Pro. */
  lockedPlan?: string
}

export function TokenSelect<T extends string>({
  value,
  onChange,
  options,
  placeholder,
  className,
}: {
  value: T
  onChange: (value: T) => void
  options: TokenSelectOption<T>[]
  placeholder?: string
  className?: string
}) {
  return (
    <Select value={value} onValueChange={(v) => onChange(v as T)}>
      <SelectTrigger className={cn(ROW_TRIGGER_CLASS, className)}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent className={SELECT_CONTENT_CLASS}>
        {options.map((o) => (
          <PlanLockTooltip
            key={o.value}
            locked={!!o.locked}
            feature={o.label}
            plan={o.lockedPlan}
            className="block"
          >
            <SelectItem value={o.value} disabled={o.disabled}>
              {o.locked ? (
                <span className="flex items-center gap-1.5">
                  {o.label}
                  <PlanStar />
                </span>
              ) : (
                o.label
              )}
            </SelectItem>
          </PlanLockTooltip>
        ))}
      </SelectContent>
    </Select>
  )
}

/**
 * Searchable single-select for large option sets. Rendering the full ~6k
 * instrument universe as eager <SelectItem> nodes locks the main thread every
 * time the dropdown opens; this keeps a visible search box and only ever renders
 * the first SEARCHABLE_MAX_RESULTS matches so the user narrows the list by typing.
 */
const SEARCHABLE_MAX_RESULTS = 20

export function SearchableSelect({
  value,
  onChange,
  options,
  placeholder,
  className,
  emptyText,
}: {
  value: string
  onChange: (v: string) => void
  options: Option<string>[]
  placeholder?: string
  className?: string
  emptyText?: string
}) {
  const t = useTranslations('orderForm.conditionEditor')
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  const { matches, total } = useMemo(() => {
    const q = query.trim().toLowerCase()
    const all = q
      ? options.filter(
          (o) => o.label.toLowerCase().includes(q) || o.value.toLowerCase().includes(q)
        )
      : options
    return { matches: all.slice(0, SEARCHABLE_MAX_RESULTS), total: all.length }
  }, [options, query])

  const hidden = total - matches.length
  const selected = options.find((o) => o.value === value)
  const triggerLabel = selected?.label || value || placeholder

  const commit = (v: string) => {
    onChange(v)
    setOpen(false)
    setQuery('')
  }

  // Seed the search box with the current value so a typo can be corrected in
  // place; select-all means the first keystroke replaces it for a fresh search.
  const openAndSeed = () => {
    setQuery(value)
    setOpen(true)
    requestAnimationFrame(() => inputRef.current?.select())
  }

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        if (next) openAndSeed()
        else {
          setOpen(false)
          setQuery('')
        }
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(ROW_TRIGGER_CLASS, 'flex items-center justify-between gap-2', className)}
        >
          <span
            className={cn('flex-1 truncate text-right', !selected && !value && 'text-white/20')}
          >
            {triggerLabel}
          </span>
          <ChevronsUpDownIcon className="size-3.5 shrink-0 text-white/40" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className={cn(SELECT_CONTENT_CLASS, 'w-[--radix-popover-trigger-width] p-0')}
        onOpenAutoFocus={(e) => {
          e.preventDefault()
          inputRef.current?.focus()
        }}
      >
        <div className="flex items-center gap-2 px-2.5">
          <SearchIcon className="size-3.5 shrink-0 text-white/30" />
          <input
            ref={inputRef}
            type="text"
            data-1p-ignore
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            // Stop printable keys from reaching the global shortcut handler
            // (e.g. "c" → switch to chat) while the user is searching.
            onKeyDown={(e) => {
              e.stopPropagation()
              if (e.key === 'Enter' && matches[0]) {
                e.preventDefault()
                commit(matches[0].value)
              }
            }}
            placeholder={t('search')}
            className="h-9 w-full bg-transparent text-sm text-white outline-none placeholder:text-white/20"
          />
        </div>
        <div className="max-h-67.5 overflow-y-auto py-1">
          {matches.length === 0 ? (
            <div className="px-2.5 py-6 text-center text-xs text-white/30">
              {emptyText ?? t('noMatches')}
            </div>
          ) : (
            <>
              {matches.map((o) => {
                const active = o.value === value
                return (
                  <button
                    key={o.value}
                    type="button"
                    onClick={() => commit(o.value)}
                    className={cn(
                      'flex h-8 w-full items-center justify-between gap-2 px-2.5 text-sm text-white/80 hover:bg-white/5',
                      active && 'text-white'
                    )}
                  >
                    <span className="truncate">{o.label}</span>
                    {active && <CheckIcon className="size-3.5 shrink-0" />}
                  </button>
                )
              })}
              {hidden > 0 && (
                <div className="px-2.5 py-1.5 text-center text-xs text-white/30">
                  {t('moreKeepTyping', { count: hidden })}
                </div>
              )}
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}

/** Operator select with a cross-above/below explainer when those ops are chosen. */
export function OperatorSelect({
  value,
  onChange,
}: {
  value: OperatorToken
  onChange: (v: OperatorToken) => void
}) {
  const t = useTranslations('orderForm.conditionEditor')
  const help =
    value === 'cross_above'
      ? t('operatorHelp.crossAbove')
      : value === 'cross_below'
        ? t('operatorHelp.crossBelow')
        : null
  const options = OPERATOR_OPTIONS.map((o) => ({
    ...o,
    label: t(`operators.${o.value}`),
  }))
  return (
    <div className="flex w-full items-center gap-1.5">
      {help && <InfoTip text={help} />}
      <TokenSelect value={value} onChange={onChange} options={options} />
    </div>
  )
}

/** Symbol select backed by available instruments; falls back to a free-text box. */
export function SymbolSelect({
  value,
  onChange,
  placeholder,
}: {
  value: string
  onChange: (v: string) => void
  placeholder?: string
}) {
  const t = useTranslations('orderForm.conditionEditor')
  const { symbolOptions, defaultSymbol } = useConditionEditorData()
  const resolvedPlaceholder = placeholder ?? t('labels.symbol')
  const resolvedValue = value || defaultSymbol
  // Ensure the current value (and the default) are always selectable. Memoized
  // so the full instrument list isn't re-spread on every render.
  const merged = useMemo<Option<string>[]>(() => {
    const out = [...symbolOptions]
    for (const v of [resolvedValue]) {
      if (v && !out.some((o) => o.value === v)) out.unshift({ value: v, label: formatSymbol(v) })
    }
    return out
  }, [symbolOptions, resolvedValue])
  if (!merged.length) {
    return <TextInput value={value} onChange={onChange} placeholder={resolvedPlaceholder} />
  }
  return (
    <SearchableSelect
      value={resolvedValue}
      onChange={onChange}
      options={merged}
      placeholder={resolvedPlaceholder}
    />
  )
}

/** Asset / underlying / coin select backed by base assets; free-text fallback. */
export function AssetSelect({
  value,
  onChange,
  placeholder,
}: {
  value: string
  onChange: (v: string) => void
  placeholder?: string
}) {
  const t = useTranslations('orderForm.conditionEditor')
  const { assetOptions } = useConditionEditorData()
  const resolvedPlaceholder = placeholder ?? t('labels.asset')
  const merged = useMemo<Option<string>[]>(() => {
    const out = [...assetOptions]
    if (value && !out.some((o) => o.value === value)) out.unshift({ value, label: value })
    return out
  }, [assetOptions, value])
  if (!merged.length) {
    return <TextInput value={value} onChange={onChange} placeholder={resolvedPlaceholder} />
  }
  return (
    <SearchableSelect
      value={value}
      onChange={onChange}
      options={merged}
      placeholder={resolvedPlaceholder}
    />
  )
}

/** Position-group select backed by available groups; free-text fallback. */
export function GroupSelect({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const t = useTranslations('orderForm.conditionEditor')
  const { groupOptions } = useConditionEditorData()
  const merged = useMemo<Option<string>[]>(() => {
    const opts = [...groupOptions]
    if (value && !opts.some((o) => o.value === value)) opts.unshift({ value, label: value })
    return opts
  }, [groupOptions, value])
  if (!merged.length) {
    return <TextInput value={value} onChange={onChange} placeholder={t('positionGroupId')} />
  }
  return (
    <TokenSelect
      value={value}
      onChange={onChange}
      options={merged}
      placeholder={t('labels.group')}
    />
  )
}

/** Account select backed by the accounts available to the current order form. */
export function AccountSelect({
  value,
  onChange,
}: {
  value?: string
  onChange: (v: string) => void
}) {
  const t = useTranslations('orderForm.conditionEditor')
  const { accountOptions, defaultAccountId } = useConditionEditorData()
  const resolvedValue = value || defaultAccountId
  const options = useMemo<Option<string>[]>(() => {
    const out = [...accountOptions]
    if (resolvedValue && !out.some((o) => o.value === resolvedValue)) {
      out.unshift({ value: resolvedValue, label: resolvedValue })
    }
    return out
  }, [accountOptions, resolvedValue])

  return (
    <TokenSelect
      value={resolvedValue}
      onChange={onChange}
      options={options}
      placeholder={t('labels.account')}
    />
  )
}

/** Candle / returns timeframe select (value is seconds as a string). */
export function TimeframeSelect({
  value,
  onChange,
}: {
  value: string
  onChange: (v: string) => void
}) {
  const t = useTranslations('orderForm.conditionEditor')
  const options = useMemo<Option<string>[]>(() => {
    const opts = TIME_FRAME_OPTIONS.map((t) => ({ value: String(t.value), label: t.label }))
    if (value && !opts.some((o) => o.value === value)) opts.unshift({ value, label: `${value}s` })
    return opts
  }, [value])
  return (
    <TokenSelect
      value={value}
      onChange={onChange}
      options={options}
      placeholder={t('labels.timeframe')}
    />
  )
}

// ── Inputs ────────────────────────────────────────────────────────────────────

export function TextInput({
  value,
  onChange,
  placeholder,
}: {
  value: string
  onChange: (v: string) => void
  placeholder?: string
}) {
  return (
    <input
      type="text"
      data-1p-ignore
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={ROW_INPUT_CLASS}
    />
  )
}

export function DecimalInput({
  value,
  onChange,
  placeholder = '0',
  signed = false,
  suffix,
}: {
  value: string
  onChange: (v: string) => void
  placeholder?: string
  signed?: boolean
  suffix?: string
}) {
  const numberInput = useNumberInput(value, { allowNegative: signed })
  const handle = (raw: string) => {
    const val = numberInput.parse(raw)
    if (val !== null) onChange(val)
  }
  if (!suffix) {
    return (
      <input
        type="text"
        inputMode="decimal"
        data-1p-ignore
        value={numberInput.display}
        onChange={(e) => handle(e.target.value)}
        placeholder={placeholder}
        className={ROW_INPUT_CLASS}
      />
    )
  }
  return (
    <FormInput
      inputMode="decimal"
      value={numberInput.display}
      onChange={(e) => handle(e.target.value)}
      placeholder={placeholder}
      unit={suffix}
    />
  )
}

// ── Chip input (db_news facets) ───────────────────────────────────────────────
// Free-text tag input. When `suggestions` is supplied (from the DB News
// vocabulary), a type-ahead dropdown offers matching values — but the input stays
// free text: the user can always type a value that isn't in the list, since the
// backend matches DB News filters by plain string.
export function ChipInput({
  values,
  onChange,
  placeholder,
  suggestions,
  renderChipPreview,
}: {
  values: string[]
  onChange: (next: string[]) => void
  placeholder?: string
  suggestions?: string[]
  /** Optional per-chip wrapper (e.g. a hover/focus preview popover). The chip's
   * own React key stays owned by ChipInput regardless of what this returns. */
  renderChipPreview?: (chip: string, content: ReactNode) => ReactNode
}) {
  const [draft, setDraft] = useState('')
  const [focused, setFocused] = useState(false)
  const anchorRef = useRef<HTMLDivElement>(null)

  const addValue = (raw: string) => {
    const v = raw.trim()
    if (v && !values.includes(v)) onChange([...values, v])
    setDraft('')
  }

  const matches = useMemo(
    () => filterChipSuggestions(suggestions, draft, values),
    [suggestions, draft, values]
  )
  const open = focused && matches.length > 0

  return (
    <Popover open={open} onOpenChange={(next) => !next && setFocused(false)}>
      <PopoverAnchor asChild>
        <div
          ref={anchorRef}
          className={cn(
            ORDER_FORM_FIELD_CLASS,
            'flex h-auto min-h-9 w-full flex-wrap items-center gap-1 px-2 py-1.5'
          )}
        >
          {values.map((chip) => {
            const content = (
              <span
                tabIndex={0}
                className="bg-foreground/10 text-foreground/80 flex items-center gap-1 px-1.5 py-0.5 text-xs outline-none"
              >
                {chip}
                <Button
                  variant="ghost"
                  size="icon"
                  type="button"
                  aria-label={`Remove ${chip}`}
                  onClick={() => onChange(values.filter((c) => c !== chip))}
                  className="text-white/40 hover:text-white/80"
                >
                  <XIcon className="size-3" />
                </Button>
              </span>
            )
            return (
              <Fragment key={chip}>
                {renderChipPreview ? renderChipPreview(chip, content) : content}
              </Fragment>
            )
          })}
          <input
            type="text"
            data-1p-ignore
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onFocus={() => setFocused(true)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ',') {
                e.preventDefault()
                addValue(draft)
              } else if (e.key === 'Backspace' && !draft && values.length) {
                onChange(values.slice(0, -1))
              }
            }}
            onBlur={() => {
              addValue(draft)
              setFocused(false)
            }}
            placeholder={values.length ? '' : placeholder}
            className="min-w-16 flex-1 bg-transparent text-sm text-white outline-none placeholder:text-white/20"
          />
        </div>
      </PopoverAnchor>
      <PopoverContent
        align="start"
        // Keep focus in the input across open/close so the user can keep typing
        // (and so closing on a no-match keystroke never blurs-and-commits the draft).
        onOpenAutoFocus={(e) => e.preventDefault()}
        onCloseAutoFocus={(e) => e.preventDefault()}
        // The field is a PopoverAnchor, not a PopoverTrigger, so Radix's
        // dismissable layer counts pointer/focus events on the input and chips as
        // "outside" and closes the list mid-typing. Keep it open while the
        // interaction is inside the field; a genuine outside click still blurs the
        // input (onBlur → setFocused(false)) and closes it.
        onInteractOutside={(e) => {
          if (anchorRef.current?.contains(e.target as Node)) e.preventDefault()
        }}
        className={cn(SELECT_CONTENT_CLASS, 'w-[--radix-popover-trigger-width] p-0')}
      >
        <div className="max-h-67.5 overflow-y-auto py-1">
          {matches.map((s) => (
            <button
              key={s}
              type="button"
              // preventDefault on mousedown keeps the input focused, so onBlur
              // doesn't fire (and unmount the list) before the click registers.
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => addValue(s)}
              className="flex h-8 w-full items-center px-2.5 text-left text-sm text-white/80 hover:bg-white/5"
            >
              <span className="truncate">{s}</span>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  )
}
